import { bezier, fontStack, ms, num, px, type TokenValue } from './value.ts';

/**
 * The theme-independent half of §9: everything that does *not* change when the theme does.
 *
 * A value earns a place here by being referenced from more than one screen or by encoding a decision
 * that was measured rather than picked. Numbers used once, in one stylesheet, stay in that
 * stylesheet — a token per declaration is an indirection layer, not a design system.
 */

/** §9's scale, verbatim. Referenced by index because that is what it is — a scale, not five names. */
export const space = [px(0), px(4), px(8), px(12), px(16), px(24), px(32), px(48), px(64)] as const;

export const radius = {
  sm: px(4),
  md: px(8),
  lg: px(16),
  /**
   * Not in §9's three, and not a size: `pill` is a *shape* — the sort chips, the jump-out links and
   * the reply toggle are all "as round as they are tall", which no fixed radius expresses.
   */
  pill: px(999),
} as const;

/**
 * §9 names three steps (body 16, title 20, display 28) and those keep their exact values. The five
 * additions are the ones the app already had and could not name: a badge, a timestamp, an eyebrow, a
 * section heading and the header's menu are real sizes on screen, and a three-step scale forced them to
 * be hard-coded px in five stylesheets.
 */
export const text = {
  micro: px(11),
  caption: px(12),
  small: px(13),
  /**
   * The header's main menu items, and nothing else. Its own step rather than `small` because the
   * nav is the one row of type whose size is a *layout* decision — five destinations, a search box and
   * the account chip have to share one line — so it has to be tunable without moving every timestamp,
   * badge and caption that also reads at 13.
   */
  nav: px(14),
  body: px(16),
  lead: px(18),
  title: px(20),
  display: px(28),
} as const;

/**
 * The 10-foot scale, defined on day one exactly as §9 insists — one object literal now instead of
 * the "we reused phone sizes on TV" discovery in phase 5. Every step is ≥ its web counterpart and a
 * test asserts it, so this cannot rot into a copy of the table above.
 *
 * It is not aspirational either: the emitter writes it as a `[data-scale='tv']` block, so stamping
 * one attribute on `<html>` re-scales the whole app in a browser today.
 */
export const textTv = {
  micro: px(14),
  caption: px(16),
  small: px(18),
  nav: px(20),
  body: px(20),
  lead: px(24),
  title: px(28),
  display: px(40),
} as const;

/** §9's TV safe area. Padding, not margin — the background still has to reach the bezel. */
export const overscan = { h: px(48), v: px(27) } as const;

export const leading = {
  /** Headings: a title that wraps must not read as two paragraphs. */
  tight: num(1.25),
  snug: num(1.35),
  normal: num(1.5),
} as const;

export const weight = {
  regular: num(400),
  medium: num(500),
  semibold: num(600),
  bold: num(700),
} as const;

/**
 * No webfont, deliberately: 4d's offline shell would have to cache it, this project loads nothing
 * from a CDN, and a font that arrives late is a layout shift on every first paint.
 *
 * The **mono stack is the design decision**, not a fallback for code. Every number the app shows is
 * a measurement someone compares — a timecode against a duration, a chapter start against the
 * playhead, a check's verdict against the one above it — so all of them are set in mono with
 * tabular figures, and prose is not. That is the one typographic signature here.
 */
export const font = {
  sans: fontStack(
    'ui-sans-serif',
    'system-ui',
    '-apple-system',
    'Segoe UI',
    'Roboto',
    'Helvetica Neue',
    'Arial',
    'sans-serif',
  ),
  mono: fontStack(
    'ui-monospace',
    'SF Mono',
    'JetBrains Mono',
    'Cascadia Mono',
    'Menlo',
    'Consolas',
    'monospace',
  ),
} as const;

export const border = { hairline: px(1), thick: px(2) } as const;

/** §9's one focus model: the same ring on every interactive element, at the same offset. */
export const focus = { width: px(2), offset: px(2) } as const;

/**
 * `fast`/`base`/`slow` are transition lengths. `skipFeedback` is not one of those and is here for
 * `previewWidth`'s reason: it is a **lifetime** that two files have to agree on — the watch hook's
 * timer takes the skip disc away after it, and the disc's own keyframes have to have finished fading
 * by then, or a press at 950 ms swaps a fully lit disc for a fresh one mid-fade.
 */
export const duration = {
  fast: ms(120),
  base: ms(160),
  slow: ms(240),
  skipFeedback: ms(1100),
} as const;

export const ease = {
  /** Quick to leave, soft to land. */
  standard: bezier(0.2, 0.05, 0.1, 1),
  exit: bezier(0.4, 0, 1, 1),
} as const;

/**
 * Layout constants that more than one file has to agree on. Three of them are measured decisions
 * carried over from phase 1 rather than tastes:
 *
 * - `railWidth` — §8's recommendations column. The rail moves beside the player only when there is
 *   room for a 16:9 video *and* this width, which is what `breakpoints.wide` is.
 * - `previewWidth` — the storyboard tile is drawn at this width in the *tooltip* and cropped to it
 *   in **core's** arithmetic (`storyboardPreview`), so the two must be the same number. It was a
 *   constant in `PlayerChrome.tsx`; here it can be read by the CSS as well.
 * - `railThumbWidth` — the same 168 px by coincidence, not by design, so it is its own name.
 */
export const layout = {
  headerHeight: px(56),
  /**
   * The two control heights, which are also the side of an icon-only button at each size — 36 px is
   * the floor a pointer needs and 28 px is the compact step a card corner uses. Named here rather
   * than left in `button.module.css` because a second stylesheet has to line a box up with one: a
   * grid card's corner strip is exactly one `sm` icon button wide, and holding it to that width is
   * what stops the strip resizing when a remove button puts its confirm word on.
   */
  controlSm: px(28),
  controlMd: px(36),
  /** The bottom tab bar the nav becomes below `md` — the `main` area pads by it so nothing hides under it. */
  tabBarHeight: px(56),
  contentMax: px(1100),
  wideMax: px(1500),
  /**
   * `breakpoints.uw` and up: `feedCardMin` (260 px) plus the grid's own column gap is ~276 px, so
   * 300 px past `wideMax` is room for one more `auto-fill` column rather than a wider margin either
   * side of the same four. Consumed by `ui/layout.module.css`'s `.wide` utility only — the Watch
   * page's own wide/theater arithmetic stays on `wideMax`, unraised, since nothing there is
   * short on room the way a card grid is.
   */
  ultraWideMax: px(1800),
  railWidth: px(372),
  /**
   * The shortest the Watch player may get when the viewport-height cap squeezes it — below this a
   * 16:9 picture stops being watchable and the transport bar covers most of it. Applied as a
   * `min-height` in and out of theater, so it is the floor under every height rule the stage has.
   */
  playerMinHeight: px(150),
  previewWidth: px(168),
  railThumbWidth: px(168),
  /**
   * The narrowest a feed card may be before the grid drops a column. A *minimum* rather than a
   * width, because the grid is `repeat(auto-fill, minmax(…, 1fr))` — hard-wiring a column count to a
   * viewport breakpoint would break under `[data-scale='tv']`, where the type scale grows 25 % and
   * the same four columns no longer fit their own titles.
   */
  feedCardMin: px(260),
  /**
   * The narrowest a *channel* card may be — the manage-subscriptions grid. Smaller than a feed
   * card's minimum because the tile is an avatar over a name, not a 16:9 frame over a title.
   */
  channelCardMin: px(160),
  /**
   * The minimized player's popover. Wide enough that a 16:9 picture is still readable at a
   * glance and narrow enough that it never covers a whole feed column — at `feedCardMin` + a gutter
   * the grid's last card stays legible beside it.
   */
  miniPlayerWidth: px(320),
  /**
   * The same popover from `breakpoints.lg` up, where it stops being a compromise. `lg` now,
   * and `wide` before it: the old step was justified by "room for a 16:9 player *and* the
   * 372 px rail", which is a fact about the **watch page**, and this box is never on it. `lg` is
   * where the menu leaves the bottom of the viewport for the header, i.e. where the app stops
   * wearing its phone shape, and a viewport that wide has room for a corner player somebody can
   * actually watch rather than merely identify. Still under half the 1024 px that earns the step.
   */
  miniPlayerWideWidth: px(420),
  /**
   * The video tile inside the minimized player's **bar** form, below `md`. 16:9 at this width is
   * 63 px tall, which is what sets the bar's own height — the controls beside it are icon buttons at
   * the default `md` size, 36 px, and fit inside that with 13 px to spare either way.
   */
  miniBarTileWidth: px(112),
} as const;

/**
 * One stacking order for the whole app. The four `media*` layers are *inside* `.player`, which is
 * also the fullscreen element — they are deliberately tiny so that nothing in the player can ever
 * paint over the header, and equally so that the header cannot paint over fullscreen video.
 */
export const z = {
  media: num(0),
  mediaChrome: num(1),
  mediaTip: num(2),
  mediaMenu: num(3),
  header: num(100),
  popover: num(200),
  skipLink: num(300),
} as const;

/**
 * Breakpoints are **data, not custom properties**, because a `var()` cannot be used in a media
 * query — so the px values are written literally in the stylesheets and a test asserts that every
 * `min-width`/`max-width` in the web app is one of these. That check is what keeps this list the
 * single source of truth instead of a well-meant comment.
 *
 * `wide` is the load-bearing one: it is not a device size but "room for a 16:9 player *and* the
 * 372 px rail", measured in a browser.
 */
export const breakpoints = {
  /** Phone landscape and up: the nav can be a row rather than a sheet. */
  sm: 480,
  /** The icon nav rail appears. */
  md: 720,
  /** Labels appear beside the nav icons. */
  lg: 1024,
  /** The recommendations rail moves beside the player. */
  wide: 1120,
  /** Wide desktop: the shell stops growing and centres. */
  xl: 1440,
  /**
   * A 1080p monitor and up. `wideMax` (1500 px) is a flat ceiling above `wide`, chosen for a 372 px
   * rail beside a 16∶9 player — a grid page hits it too and stops there, which is `layout.wideMax`'s
   * own comment's "three 340 px cards on a 2560 px display, two thirds of it empty". One more step
   * up, `ultraWideMax` gives a grid page room for another card's width past that ceiling rather than
   * leaving it as margin — a genuinely wider screen, not merely a taller `wide` reading, which is why
   * this is its own breakpoint instead of raising `wide`'s own numbers.
   */
  uw: 1920,
} as const;

/** Everything above, as one tree — the shape `flattenTokens` walks. */
export const scales = {
  space,
  radius,
  text,
  leading,
  weight,
  font,
  border,
  focus,
  duration,
  ease,
  layout,
  z,
  overscan,
} as const;

export type ScaleTree = typeof scales;

/** Proof for the reader (and the type checker) that every scale value is a `TokenValue`. */
export type ScaleValue = TokenValue;
